import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Paper,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import Search from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { getDashboardLinkKey, normalizeDashboardGroups } from "./dashboardGroups";

/**
 * Shared dashboard card-grid and searchable options menu shell.
 * Used by PG, Internal User, and Law Firm dashboard shells.
 */
export default function DashboardActionGrid({
  links,
  sectionTitle,
  title,
  variant = "pg",
  groupTitleFontWeight = 500,
}) {
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTitle, setMenuTitle] = useState("");
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuSearch, setMenuSearch] = useState("");

  const groups = normalizeDashboardGroups(links, { title, sectionTitle, variant });
  const supportDisabledOptions = variant === "internal";

  const handleActionClick = (link, event) => {
    if (supportDisabledOptions && link?.options) {
      setMenuAnchorEl(event.currentTarget);
      setMenuOptions(link.options);
      setMenuTitle(link.name || "Options");
      setMenuSearch("");
      return;
    }
    const hasOptions = Array.isArray(link?.options) && link.options.length > 0;
    if (hasOptions) {
      setMenuAnchorEl(event.currentTarget);
      setMenuOptions(link.options);
      setMenuTitle(link.name || "Options");
      setMenuSearch("");
      return;
    }
    if (link?.path) navigate(link.path);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuOptions([]);
    setMenuTitle("");
    setMenuSearch("");
  };

  const filteredOptions = menuOptions.filter(
    (opt) =>
      !menuSearch || String(opt?.name || "").toLowerCase().includes(menuSearch.toLowerCase())
  );

  return (
    <>
      <Box
        width="100%"
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
        sx={{ pb: 1 }}
      >
        {groups.map((group) => (
          <Paper
            key={group.title}
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 1,
              background: (theme) =>
                theme.palette.mode === "light"
                  ? "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)"
                  : theme.palette.background.paper,
              border: "1px solid rgba(37, 99, 235, 0.2)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: groupTitleFontWeight,
                  color: "#333333",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                {group.title}
              </Typography>
            </Box>
            {group.description ? (
              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  mb: 1.5,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {group.description}
              </Typography>
            ) : null}
            <Box
              display="flex"
              flexDirection="row"
              sx={{
                gap: { xs: 1, sm: 1.5 },
                flexWrap: "wrap",
                pb: 0.5,
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {group.items.map((link) => (
                <Button
                  key={getDashboardLinkKey(link)}
                  variant="outlined"
                  size="medium"
                  onClick={(e) => handleActionClick(link, e)}
                  startIcon={
                    link.icon
                      ? React.cloneElement(link.icon, {
                          style: { fontSize: { xs: 16, sm: 20 } },
                        })
                      : null
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    px: { xs: 1.5, sm: 2.5 },
                    py: { xs: 0.75, sm: 1.25 },
                    color: "#2563eb",
                    borderColor: "rgba(37, 99, 235, 0.3)",
                    whiteSpace: "nowrap",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    minWidth: { xs: "auto", sm: "auto" },
                    flex: { xs: "0 0 auto", sm: "none" },
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {link.name}
                </Button>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 320,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            },
          },
        }}
      >
        <Box px={2} pt={2} pb={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {menuTitle}
          </Typography>
          <TextField
            value={menuSearch}
            onChange={(e) => setMenuSearch(e.target.value)}
            placeholder="Search..."
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 320, overflowY: "auto", py: 0.5 }}>
          {filteredOptions.map((opt, i) => {
            const isLocked = supportDisabledOptions && Boolean(opt?.disabled);
            return (
              <MenuItem
                key={`${opt?.name || "option"}-${i}`}
                disabled={isLocked}
                onClick={() => {
                  if (isLocked) return;
                  if (opt?.path) navigate(opt.path);
                  handleCloseMenu();
                }}
                sx={
                  isLocked
                    ? {
                        cursor: "not-allowed",
                        bgcolor: "action.hover",
                        "&.Mui-disabled": { opacity: 1 },
                        "& .MuiListItemText-primary": {
                          color: "text.disabled",
                          fontWeight: 500,
                        },
                        "& .MuiListItemIcon-root": { color: "action.disabled" },
                      }
                    : undefined
                }
              >
                {opt?.icon ? (
                  <ListItemIcon sx={isLocked ? { color: "action.disabled" } : undefined}>
                    {React.cloneElement(opt.icon, { fontSize: "small" })}
                  </ListItemIcon>
                ) : null}
                <ListItemText
                  primary={opt?.name || "Open"}
                  secondary={isLocked && opt?.disabledHint ? opt.disabledHint : undefined}
                  slotProps={{
                    secondary: { variant: "caption", sx: { whiteSpace: "normal" } },
                  }}
                />
              </MenuItem>
            );
          })}
          {filteredOptions.length === 0 ? (
            <Box px={2} py={1}>
              <Typography variant="body2" color="text.secondary">No matches</Typography>
            </Box>
          ) : null}
        </Box>
      </Menu>
    </>
  );
}

DashboardActionGrid.propTypes = {
  links: PropTypes.array,
  sectionTitle: PropTypes.string,
  title: PropTypes.string,
  variant: PropTypes.oneOf(["pg", "internal", "lawFirm"]),
  groupTitleFontWeight: PropTypes.number,
};
